import { useId, type ReactNode, type RefObject } from "react";
import { X } from "lucide-react";
import { useModalFocus } from "../hooks/useModalFocus";

interface ModalShellProps {
  title: string;
  description?: string;
  onClose: () => void;
  /** Element to focus on open. Destructive dialogs pass their Cancel button. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  size?: "sm" | "md" | "lg" | "xl";
  footer?: ReactNode;
  children: ReactNode;
}

const SIZE: Record<NonNullable<ModalShellProps["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
};

/** Centered accessible dialog: role, labelling, focus trap, Escape, backdrop click. */
export function ModalShell({ title, description, onClose, initialFocusRef, size = "md", footer, children }: ModalShellProps) {
  const containerRef = useModalFocus(onClose, initialFocusRef);
  const titleId = useId();
  const descriptionId = useId();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={`flex max-h-[90vh] w-full flex-col rounded-2xl bg-white shadow-lg focus:outline-none ${SIZE[size]}`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-brand-navy">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-sm text-gray-600">
                {description}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close dialog" className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-green-50">
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}
